<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  tabs: {
    type: Array,
    default: () => [],
  },
  selectedTab: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['tabChange']);

const activeTab = ref(props.selectedTab);

watch(
  () => props.selectedTab,
  (value, oldValue) => {
    if (value !== oldValue) {
      activeTab.value = props.selectedTab;
    }
  }
);

const onTabChange = index => {
  activeTab.value = index;
  emit('tabChange', props.tabs[index].key);
};
</script>

<template>
  <nav class="search-tabs" :aria-label="$t('SEARCH.INPUT_PLACEHOLDER')">
    <button
      v-for="(item, index) in tabs"
      :key="item.key"
      type="button"
      class="search-tab"
      :class="{ 'is-active': activeTab === index }"
      :aria-current="activeTab === index ? 'page' : undefined"
      @click="onTabChange(index)"
    >
      <span class="search-tab__label">{{ item.name }}</span>
      <span v-if="item.showBadge" class="search-tab__count">
        {{ item.count }}
      </span>
    </button>
  </nav>
</template>

<style scoped>
.search-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  max-width: 100%;
  padding: 2px;
}

.search-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 6px 12px;
  margin: 0;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: var(--wlab-text-muted, var(--n-slate-11));
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
}

.search-tab:hover {
  background: color-mix(
    in srgb,
    var(--wlab-text-primary, #fff) 6%,
    transparent
  );
  color: var(--wlab-text-primary, var(--n-slate-12));
}

.search-tab.is-active {
  border-color: color-mix(
    in srgb,
    var(--wlab-accent, #6366f1) 35%,
    transparent
  );
  background: color-mix(in srgb, var(--wlab-accent, #6366f1) 14%, transparent);
  color: var(--wlab-accent, #6366f1);
}

.search-tab__count {
  display: inline-flex;
  min-width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border-radius: 999px;
  background: color-mix(
    in srgb,
    var(--wlab-text-primary, #fff) 8%,
    transparent
  );
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.search-tab.is-active .search-tab__count {
  background: color-mix(in srgb, var(--wlab-accent, #6366f1) 18%, transparent);
  color: var(--wlab-accent, #6366f1);
}
</style>
